import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import "./ActionsMenu.css";

/**
 * Shared ActionsMenu component - renders a trigger button with an ellipsis icon
 * and a dropdown menu of action items.
 *
 * The dropdown is rendered in a portal with fixed positioning so it is never
 * clipped by an ancestor's `overflow` (e.g. a horizontally scrollable table).
 *
 * @param {Array} actions - Array of action objects: { label, icon, onClick, danger, disabled, dividerBefore, title, description }
 * @param {boolean} openUpwards - Whether the dropdown opens upward (default: false)
 * @param {object} style - Optional style overrides for the root container
 * @param {Array} buttonClassNames - Additional CSS class names to apply to the trigger button
 *
 * Usage:
 *   <ActionsMenu
 *     actions={[
 *       { label: "Edit", icon: "fal fa-pencil", onClick: handleEdit },
 *       { label: "Delete", icon: "fal fa-trash-alt", onClick: handleDelete, danger: true },
 *     ]}
 *     openUpwards={false}
 *   />
 */
const ActionsMenu = ({ actions, openUpwards = false, alignLeft = false, style, buttonClassNames = [], triggerIcon = null }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const computeCoords = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const horizontal = alignLeft
      ? { left: rect.left }
      : { right: window.innerWidth - rect.right };
    return openUpwards
      ? { ...horizontal, top: "auto", bottom: window.innerHeight - rect.top + 4 }
      : { ...horizontal, top: rect.bottom + 4, bottom: "auto" };
  }, [openUpwards, alignLeft]);

  const toggle = () => {
    if (!open) setCoords(computeCoords());
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", handlePointer);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <div className="team-actions-menu" ref={menuRef} style={style}>
      <button
        ref={buttonRef}
        className={['team-actions-btn', ...buttonClassNames].join(' ')}
        onClick={toggle}
        aria-label="Actions"
      >
        {triggerIcon || <i className="fas fa-ellipsis-v" style={{ fontSize: '16px' }}></i>}
      </button>

      {open && coords && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="team-actions-dropdown"
          style={{ position: 'fixed', margin: 0, ...coords }}
        >
          {actions.map((action) => (
            <React.Fragment key={action.label}>
              {action.dividerBefore ? <div className="team-actions-divider" /> : null}
              <button
                className={`team-actions-item${action.danger ? ' team-actions-item-danger' : ''}`}
                onClick={(e) => {
                  if (action.disabled) return;
                  setOpen(false);
                  action.onClick(e);
                }}
                disabled={action.disabled}
                title={action.title}
                style={action.disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
              >
                {React.isValidElement(action.icon)
                  ? action.icon
                  : <i className={action.icon} style={{ fontSize: '14px', width: '16px' }}></i>}
                <span>{action.label}</span>
              </button>
              {/* Visible rather than a title tooltip: a disabled button is not
                  hoverable everywhere, and the reason an action is unavailable is
                  worth reading without discovering it. */}
              {action.description && (
                <p className="team-actions-item-description">{action.description}</p>
              )}
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionsMenu;
